// Complete repair options: categories, subtypes, position codes
// Used by both Ops Console and Client Portal

export const REPAIR_CATEGORIES = {
  TIRES: {
    label: 'Tires', requiresPosition: true,
    subtypes: ['TIRE_REPLACEMENT','TIRE_REPAIR','MOUNT_BALANCE','ALIGNMENT','ROTATION','VALVE_STEM','TPMS_SENSOR','RIM_WHEEL','WHEEL_SEAL','LUG_NUT_STUD']
  },
  BRAKES: {
    label: 'Brakes', requiresPosition: true,
    subtypes: ['BRAKE_PADS','BRAKE_SHOES','ROTOR','DRUM','CALIPER','SLACK_ADJUSTER','BRAKE_CHAMBER','S_CAM','AIR_LINE_LEAK','ABS_SENSOR','BRAKE_FLUID']
  },
  SUSPENSION: {
    label: 'Suspension', requiresPosition: true,
    subtypes: ['SHOCK_ABSORBER','AIR_BAG','SPRING_LEAF','BUSHING','U_BOLT','RIDE_HEIGHT_VALVE','TORQUE_ARM','HANGER','AXLE_ALIGNMENT']
  },
  STEERING: {
    label: 'Steering', requiresPosition: false,
    subtypes: ['TIE_ROD_END','DRAG_LINK','STEERING_GEAR_BOX','POWER_STEERING_PUMP','STEERING_SHAFT_U_JOINT','KINGPIN','BALL_JOINT']
  },
  ENGINE: {
    label: 'Engine / Powertrain', requiresPosition: false,
    subtypes: ['BELT','HOSE','WATER_PUMP','ALTERNATOR','STARTER','TURBO','INJECTOR','FUEL_PUMP','OIL_LEAK_REPAIR','COOLANT_LEAK_REPAIR','ENGINE_BREATHER','SENSOR_REPLACEMENT']
  },
  COOLING: {
    label: 'Cooling', requiresPosition: false,
    subtypes: ['RADIATOR','THERMOSTAT','COOLANT_HOSE','FAN_CLUTCH','COOLANT_LEAK_REPAIR']
  },
  ELECTRICAL_LIGHTING: {
    label: 'Electrical / Lighting', requiresPosition: false,
    positionSubtypes: ['HEADLIGHT_LOW_BEAM','HEADLIGHT_HIGH_BEAM','FOG_LIGHT'],
    subtypes: ['HEADLIGHT_LOW_BEAM','HEADLIGHT_HIGH_BEAM','FOG_LIGHT','TAIL_LIGHT','BRAKE_LIGHT','TURN_SIGNAL','MARKER_LIGHT','WIRING_REPAIR','BATTERY','ALTERNATOR','FUSE_RELAY']
  },
  AIR_SYSTEM: {
    label: 'Air System', requiresPosition: false,
    subtypes: ['AIR_COMPRESSOR','AIR_DRYER','AIR_LINE','AIR_LEAK_DIAGNOSIS','VALVE_REPLACEMENT']
  },
  EXHAUST_AFTERTREATMENT: {
    label: 'Exhaust / Aftertreatment', requiresPosition: false,
    subtypes: ['DPF_REGEN','DPF_CLEANING','DEF_SYSTEM_REPAIR','EGR_SYSTEM_REPAIR','SENSOR_REPLACEMENT']
  },
  TRANSMISSION_DRIVELINE: {
    label: 'Transmission / Driveline', requiresPosition: false,
    subtypes: ['CLUTCH','U_JOINT','DRIVESHAFT','DIFFERENTIAL_SERVICE','TRANSMISSION_REPAIR']
  },
  FUEL_SYSTEM: {
    label: 'Fuel System', requiresPosition: false,
    subtypes: ['FUEL_FILTER','FUEL_LINE','FUEL_LEAK_REPAIR']
  },
  HVAC: { label: 'HVAC', requiresPosition: false, subtypes: [] },
  BODY_CHASSIS: { label: 'Body / Chassis', requiresPosition: false, subtypes: [] },
  TRAILER: { label: 'Trailer', requiresPosition: false, subtypes: [] },
  TOW_RECOVERY: {
    label: 'Tow / Recovery', requiresPosition: false,
    subtypes: ['TOW','WINCH_OUT','JUMP_START','LOCKOUT']
  },
  DIAGNOSTICS: {
    label: 'Diagnostics', requiresPosition: false,
    subtypes: ['SCAN_DIAGNOSTIC','VISUAL_INSPECTION','ROAD_TEST']
  },
  PM_SERVICE: {
    label: 'PM Service', requiresPosition: false,
    subtypes: ['OIL_CHANGE','FILTERS','GREASE_SERVICE','DOT_INSPECTION','PM_INSPECTION']
  },
  OTHER: { label: 'Other', requiresPosition: false, subtypes: [] }
};

export const CATEGORY_LIST = Object.entries(REPAIR_CATEGORIES).map(([key, val]) => ({
  value: key, label: val.label
}));

export const POSITION_CODES = [
  { code: 'FL', label: 'Front Left' },
  { code: 'FR', label: 'Front Right' },
  { code: 'RL', label: 'Rear Left' },
  { code: 'RR', label: 'Rear Right' },
  { code: 'FRONT_BOTH', label: 'Both Fronts' },
  { code: 'REAR_BOTH', label: 'Both Rears' },
  { code: 'LEFT_BOTH', label: 'Both Left' },
  { code: 'RIGHT_BOTH', label: 'Both Right' },
  { code: 'ALL_POSITIONS', label: 'All Positions' },
  { code: 'TRAILER_FL', label: 'Trailer FL' },
  { code: 'TRAILER_FR', label: 'Trailer FR' },
  { code: 'TRAILER_RL', label: 'Trailer RL' },
  { code: 'TRAILER_RR', label: 'Trailer RR' },
  { code: 'TRAILER_ALL', label: 'Trailer All' },
  { code: 'NONE', label: 'N/A' },
];

export const POSITION_REQUIRED_CATS = new Set(['TIRES', 'BRAKES', 'SUSPENSION']);
export const POSITION_CONDITIONAL_SUBTYPES = new Set(['HEADLIGHT_LOW_BEAM', 'HEADLIGHT_HIGH_BEAM', 'FOG_LIGHT']);

export function requiresPosition(category, subtype) {
  if (POSITION_REQUIRED_CATS.has(category)) return true;
  if (category === 'ELECTRICAL_LIGHTING' && POSITION_CONDITIONAL_SUBTYPES.has(subtype)) return true;
  return false;
}

export function getSubtypes(category) {
  return REPAIR_CATEGORIES[category]?.subtypes || [];
}

export function getCategoryLabel(cat) {
  return REPAIR_CATEGORIES[cat]?.label || cat;
}

export function formatSubtype(s) {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Alert severity colors
export const ALERT_COLORS = {
  critical: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', icon: '🚨' },
  warning: { bg: 'bg-amber-900/20 border-amber-800/50', text: 'text-amber-400', icon: '⚠️' },
};

// Closeout requirements
export function getCloseoutIssues(repair) {
  const issues = [];
  if (!repair?.mileage_at_repair) issues.push({ key: 'mileage', label: 'Mileage at repair required' });
  if (!repair?.invoice_uploaded && !repair?.invoice_pdf_url) issues.push({ key: 'invoice', label: 'Invoice PDF must be uploaded' });
  if (!repair?.line_items?.length) issues.push({ key: 'line_items', label: 'At least 1 line item required' });
  const missingPos = repair?.line_items?.find(li => {
    if (!requiresPosition(li.category, li.subtype)) return false;
    return !li.position_code || li.position_code === 'NONE' || li.position_code === 'UNKNOWN';
  });
  if (missingPos) issues.push({ key: 'position', label: `Position required for ${getCategoryLabel(missingPos.category)}` });
  return issues;
}
